import {
  ImageGenerationRequest,
  ImageGenerationResponse,
  ImageGenerationError,
} from '../types/imageGeneration';

/**
 * Service for handling image generation with the Swarmind API
 */
export class ImageGenerationService {
  private readonly baseUrl: string;
  private readonly apiKey: string;
  private readonly namespace: string;
  private readonly maxRetries: number = 3;
  private readonly retryDelay: number = 1000; // 1 second base delay

  constructor() {
    this.baseUrl = process.env.REACT_APP_API_URL || 'https://api.swarmind.ai';
    this.apiKey =
      localStorage.getItem('bearerToken') ||
      process.env.REACT_APP_API_KEY ||
      '';
    this.namespace = 'frontend';
  }

  private getApiKey(): string {
    return localStorage.getItem('bearerToken') || 
           process.env.REACT_APP_API_KEY || 
           this.apiKey || 
           '';
  }

  /**
   * Generate images using the Swarmind API with retry mechanism
   */
  async generateImages(
    request: ImageGenerationRequest
  ): Promise<ImageGenerationResponse> {
    return this.executeWithRetry(() => this.generateImagesInternal(request));
  }

  /**
   * Internal method for generating images (without retry logic)
   */
  private async generateImagesInternal(
    request: ImageGenerationRequest
  ): Promise<ImageGenerationResponse> {
    const url = `${this.baseUrl}/lai/testing/v1/images/generations`;

    // Handle multimodal requests with uploaded images
    let requestBody: any;
    const apiKey = this.getApiKey();
    const headers: Record<string, string> = {
      ...(apiKey ? { Authorization: `Bearer ${apiKey}` } : {}),
    };

    if (request.image) {
      // For multimodal requests, convert image to base64 and include in JSON
      const base64Image = await this.fileToBase64(request.image);
      requestBody = {
        model: request.model,
        prompt: request.prompt,
        steps: request.steps,
        n: request.n,
        size: request.size,
        image: base64Image,
        namespace: this.namespace,
      };
      headers['Content-Type'] = 'application/json';
    } else {
      // Standard text-only request
      requestBody = {
        ...request,
        namespace: this.namespace,
      };
      headers['Content-Type'] = 'application/json';
    }

    console.log('🎨 Sending image generation request:', {
      url,
      headers: {
        ...headers,
        ...(apiKey
          ? { Authorization: `Bearer ${apiKey.substring(0, 10)}...` }
          : {}),
      },
      body: request.image ? { ...requestBody, image: '[base64 data]' } : requestBody,
    });

    let response: Response;
    try {
      response = await fetch(url, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify(requestBody),
      });
    } catch (fetchError: any) {
      // Handle network errors
      const err = new Error('Network connection failed. Please check your internet connection and try again.') as ImageGenerationError;
      err.code = 'NETWORK_ERROR';
      err.details = fetchError.message || 'Fetch request failed';
      throw err;
    }

    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unknown error');
      console.error('❌ Image Generation API Error Response:', {
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(Array.from(response.headers.entries())),
        body: errorText,
      });

      const err = this.createErrorFromResponse(response.status, errorText);
      throw err;
    }

    let result: ImageGenerationResponse;
    try {
      result = await response.json();
    } catch (parseError) {
      const err = new Error('Invalid response format from server') as ImageGenerationError;
      err.code = 'INVALID_RESPONSE';
      err.details = 'Failed to parse JSON response';
      throw err;
    }

    // Validate response structure
    if (!result.data || !Array.isArray(result.data) || result.data.length === 0) {
      const err = new Error('No images were generated. Please try again.') as ImageGenerationError;
      err.code = 'EMPTY_RESPONSE';
      err.details = 'Response data array is empty or missing';
      throw err;
    }

    console.log('✅ Image generation successful:', result);
    return result;
  }

  /**
   * Create appropriate error based on HTTP status and response text
   */
  private createErrorFromResponse(status: number, errorText: string): ImageGenerationError {
    const err = new Error() as ImageGenerationError;
    err.details = errorText;

    if (status === 422) {
      const lower = (errorText || '').toLowerCase();
      if (
        lower.includes('balance') ||
        lower.includes('insufficient') ||
        lower.includes('not enough')
      ) {
        err.code = 'INSUFFICIENT_BALANCE';
        err.message = 'Insufficient balance to generate images. Please top up your account to continue.';
      } else if (
        lower.includes('no rows in result set') ||
        lower.includes('querying model id') ||
        lower.includes('model not found')
      ) {
        err.code = 'MODEL_NOT_FOUND';
        err.message = 'The selected model is not available. Please choose a different model in settings.';
      } else if (lower.includes('prompt')) {
        err.code = 'INVALID_PROMPT';
        err.message = 'Invalid prompt. Please check your text and try again.';
      } else {
        err.code = 'VALIDATION_ERROR';
        err.message = 'Request validation failed. Please check your settings and try again.';
      }
    } else if (status === 400) {
      err.code = 'INVALID_REQUEST';
      err.message = 'Invalid request parameters. Please check your input and try again.';
    } else if (status === 401) {
      err.code = 'UNAUTHORIZED';
      err.message = 'Authentication failed. Please connect your wallet and try again.';
    } else if (status === 403) {
      err.code = 'FORBIDDEN';
      err.message = 'Access denied. Please check your account permissions.';
    } else if (status === 429) {
      err.code = 'RATE_LIMITED';
      err.message = 'Too many requests. Please wait a moment and try again.';
    } else if (status >= 500) {
      err.code = 'SERVER_ERROR';
      err.message = 'Server error occurred. Please try again in a few moments.';
    } else {
      err.code = 'UNKNOWN_ERROR';
      err.message = `Request failed with status ${status}. Please try again.`;
    }

    return err;
  }

  /**
   * Execute a function with exponential backoff retry logic
   */
  private async executeWithRetry<T>(
    operation: () => Promise<T>,
    attempt: number = 1
  ): Promise<T> {
    try {
      return await operation();
    } catch (error: any) {
      const shouldRetry = this.shouldRetryError(error, attempt);
      
      if (shouldRetry && attempt < this.maxRetries) {
        const delay = this.calculateRetryDelay(attempt);
        console.log(`🔄 Retrying image generation (attempt ${attempt + 1}/${this.maxRetries}) after ${delay}ms`);
        
        await this.sleep(delay);
        return this.executeWithRetry(operation, attempt + 1);
      }
      
      // Add retry information to error message if we exhausted retries
      if (attempt >= this.maxRetries && this.shouldRetryError(error, attempt)) {
        error.message = `${error.message} (Failed after ${this.maxRetries} attempts)`;
      }
      
      throw error;
    }
  }

  /**
   * Determine if an error should trigger a retry
   */
  private shouldRetryError(error: any, attempt: number): boolean {
    if (attempt >= this.maxRetries) return false;
    
    // Don't retry client errors that won't be fixed by retrying
    const nonRetryableCodes = [
      'INSUFFICIENT_BALANCE',
      'MODEL_NOT_FOUND', 
      'INVALID_REQUEST',
      'INVALID_PROMPT',
      'VALIDATION_ERROR',
      'UNAUTHORIZED',
      'FORBIDDEN',
      'FILE_VALIDATION_ERROR'
    ];
    
    if (error.code && nonRetryableCodes.includes(error.code)) {
      return false;
    }
    
    // Retry network errors, server errors, and rate limiting
    const retryableCodes = [
      'NETWORK_ERROR',
      'SERVER_ERROR', 
      'RATE_LIMITED',
      'INVALID_RESPONSE',
      'EMPTY_RESPONSE'
    ];
    
    return error.code && retryableCodes.includes(error.code);
  }

  /**
   * Calculate delay for exponential backoff
   */
  private calculateRetryDelay(attempt: number): number {
    // Exponential backoff: 1s, 2s, 4s
    return this.retryDelay * Math.pow(2, attempt - 1);
  }

  /**
   * Sleep utility for retry delays
   */
  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Convert blockchain model ID to API model name
   */
  getModelNameFromId(modelId: string): string {
    console.log('🔄 Converting model ID for image generation:', modelId);
    return modelId;
  }

  /**
   * Resolve selected model id to backend model name using /models list
   */
  async resolveApiModelName(modelId?: string | null): Promise<string> {
    try {
      if (!modelId) {
        const { modelsService } = await import('./modelsService');
        const list = await modelsService.getAvailableModels();
        // Filter for image generation models
        const imageModels = list.filter(model => 
          (model as any).type === 'image' || 
          (model as any).capabilities?.includes('image-generation')
        );
        const first = imageModels[0] || list[0];
        const chosen = (
          first?.name ||
          first?.hfid ||
          first?.id ||
          ''
        ).toString();
        return chosen.trim();
      }
      const { modelsService } = await import('./modelsService');
      const model = await modelsService.getModelById(modelId);
      const chosen = (
        model?.name ||
        model?.hfid ||
        model?.id ||
        modelId
      ).toString();
      return chosen.trim();
    } catch (_) {
      return modelId || 'flux.1-dev'; // Default fallback model
    }
  }

  /**
   * Validate image generation request parameters
   */
  validateRequest(request: ImageGenerationRequest): void {
    if (!request.prompt || request.prompt.trim().length === 0) {
      throw new Error('Prompt is required for image generation');
    }

    if (request.steps < 1 || request.steps > 50) {
      throw new Error('Steps must be between 1 and 50');
    }

    if (request.n < 1 || request.n > 4) {
      throw new Error('Quantity must be between 1 and 4');
    }

    const validSizes = ['512x512', '768x1024', '1024x768'];
    if (!validSizes.includes(request.size)) {
      throw new Error(`Invalid image size. Must be one of: ${validSizes.join(', ')}`);
    }

    // Validate uploaded image if present
    if (request.image) {
      this.validateImageFile(request.image);
    }
  }

  /**
   * Convert file to base64 for multimodal requests with comprehensive error handling
   */
  async fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      try {
        // Validate file before processing
        this.validateImageFile(file);
        
        const reader = new FileReader();
        
        // Set up timeout to prevent hanging
        const timeout = setTimeout(() => {
          reader.abort();
          const err = new Error('File reading timed out. Please try a smaller image.') as ImageGenerationError;
          err.code = 'FILE_PROCESSING_ERROR';
          reject(err);
        }, 30000); // 30 second timeout
        
        reader.onload = () => {
          clearTimeout(timeout);
          try {
            const result = reader.result as string;
            if (!result || typeof result !== 'string') {
              const err = new Error('Failed to read file. The file may be corrupted.') as ImageGenerationError;
              err.code = 'FILE_PROCESSING_ERROR';
              reject(err);
              return;
            }
            
            // Validate data URL format
            if (!result.startsWith('data:image/')) {
              const err = new Error('Invalid image format detected. Please use a valid image file.') as ImageGenerationError;
              err.code = 'FILE_PROCESSING_ERROR';
              reject(err);
              return;
            }
            
            // Remove data URL prefix to get just the base64 data
            const base64 = result.split(',')[1];
            if (!base64 || base64.length === 0) {
              const err = new Error('Failed to extract image data. The file may be corrupted.') as ImageGenerationError;
              err.code = 'FILE_PROCESSING_ERROR';
              reject(err);
              return;
            }
            
            // Validate base64 format
            try {
              atob(base64); // Test if it's valid base64
            } catch {
              const err = new Error('Invalid image encoding detected. Please try a different image.') as ImageGenerationError;
              err.code = 'FILE_PROCESSING_ERROR';
              reject(err);
              return;
            }
            
            resolve(base64);
          } catch (error) {
            const err = new Error('Failed to process image file. Please try a different image.') as ImageGenerationError;
            err.code = 'FILE_PROCESSING_ERROR';
            err.details = error instanceof Error ? error.message : 'Unknown processing error';
            reject(err);
          }
        };
        
        reader.onerror = () => {
          clearTimeout(timeout);
          const err = new Error('Failed to read the image file. The file may be corrupted or inaccessible.') as ImageGenerationError;
          err.code = 'FILE_PROCESSING_ERROR';
          err.details = 'FileReader error event';
          reject(err);
        };
        
        reader.onabort = () => {
          clearTimeout(timeout);
          const err = new Error('File reading was cancelled. Please try again.') as ImageGenerationError;
          err.code = 'FILE_PROCESSING_ERROR';
          err.details = 'FileReader abort event';
          reject(err);
        };
        
        // Start reading the file
        try {
          reader.readAsDataURL(file);
        } catch (error) {
          clearTimeout(timeout);
          const err = new Error('Failed to start reading the file. Please try again.') as ImageGenerationError;
          err.code = 'FILE_PROCESSING_ERROR';
          err.details = error instanceof Error ? error.message : 'Unknown read error';
          reject(err);
        }
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Validate uploaded image file with comprehensive checks
   */
  validateImageFile(file: File): void {
    if (!file) {
      const err = new Error('No file provided') as ImageGenerationError;
      err.code = 'FILE_VALIDATION_ERROR';
      throw err;
    }

    // Check file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    const validExtensions = ['.jpg', '.jpeg', '.png', '.webp'];
    
    const isValidType = validTypes.includes(file.type.toLowerCase());
    const hasValidExtension = validExtensions.some(ext => 
      file.name.toLowerCase().endsWith(ext)
    );

    if (!isValidType && !hasValidExtension) {
      const err = new Error('Invalid file type. Please upload JPEG, PNG, or WebP images only.') as ImageGenerationError;
      err.code = 'FILE_VALIDATION_ERROR';
      err.details = `File type: ${file.type}, File name: ${file.name}`;
      throw err;
    }

    // Check file size
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
      const err = new Error(`File size too large (${sizeMB}MB). Please upload images smaller than 10MB.`) as ImageGenerationError;
      err.code = 'FILE_VALIDATION_ERROR';
      err.details = `File size: ${file.size} bytes`;
      throw err;
    }

    // Check minimum file size (avoid empty or corrupted files)
    const minSize = 1024; // 1KB
    if (file.size < minSize) {
      const err = new Error('File appears to be empty or corrupted. Please select a valid image file.') as ImageGenerationError;
      err.code = 'FILE_VALIDATION_ERROR';
      err.details = `File size: ${file.size} bytes`;
      throw err;
    }

    // Check file name length
    if (file.name.length > 255) {
      const err = new Error('File name is too long. Please rename the file and try again.') as ImageGenerationError;
      err.code = 'FILE_VALIDATION_ERROR';
      err.details = `File name length: ${file.name.length}`;
      throw err;
    }

    // Check for potentially problematic characters in filename
    const problematicChars = /[<>:"/\\|?*\x00-\x1f]/;
    if (problematicChars.test(file.name)) {
      const err = new Error('File name contains invalid characters. Please rename the file and try again.') as ImageGenerationError;
      err.code = 'FILE_VALIDATION_ERROR';
      err.details = `File name: ${file.name}`;
      throw err;
    }
  }
}

export const imageGenerationService = new ImageGenerationService();